import { Accordion, Button, Flex, Text, useBreakpointValue } from "@chakra-ui/react";
import { BlockHeader } from "../../../../../../../components/BlockHeader";
import React, { Fragment } from "react";
import { useTranslation } from "react-i18next";
import { CURRENCY_SYMBOLS, greenhouseGases } from "../../../../../../../utils";
import { MissingDataLabel } from "../../../../../components/MissingDataLabel";
import { QuestionAccordionItem } from "./QuestionsAndAnswers";

function GeneralQuestions({ data, handleExportClick }) {
  const { t: suppliersDataTranslation } =  useTranslation("translation", {
    keyPrefix: "reportsPage.suppliersData"
  });
  const { t } = useTranslation("translation", {
    keyPrefix: "reportsPage.suppliersData.aggregatedGeneralAndScope1And2Data.general"
  });
  const dynamicStylesProps = useBreakpointValue({
    xl: {
      includedGassesMaxWidth: 350,
      exportContainerDisplay: "flex"
    },
    lg: {
      includedGassesMaxWidth: 100,
      exportContainerDisplay: "block"
    },
    md:{
      includedGassesMaxWidth: 70,
      exportContainerDisplay: "block"
    },
    sm: {
      includedGassesMaxWidth: 50,
      exportContainerDisplay: "block"
    },
    base: {
      includedGassesMaxWidth: 30,
      exportContainerDisplay: "block"
    }
  });

  const mapGHGAnswers = (answersString) => {
    const answersArr = answersString.replaceAll(/\s/g, "").split(",");
    let includedGasses = [];

    Object.keys(greenhouseGases).forEach(key => {
      const foundGas = answersArr.find(el => el === `including${key}Emissions`);
      if (typeof foundGas !== "undefined") {
        includedGasses.push({ key: key, ...greenhouseGases[key] });
      }
    });

    const gassesText = includedGasses.map(el => el.title).join(", ");

    const gassesLabels = <Text
      fontSize="md"
      title={gassesText}
      maxWidth={`${dynamicStylesProps.includedGassesMaxWidth}ch`}
      whiteSpace={gassesText.length > dynamicStylesProps.includedGassesMaxWidth ? "nowrap" : "break-spaces"}
      overflow="hidden"
      textOverflow="ellipsis"
    >
      <b>Answer: </b>
      {includedGasses.map((element, index) => {
        return <Fragment key={element.key}>
          {element.label}
          {index < includedGasses.length - 1 ? `, ` : ""}
        </Fragment>;
      })}
    </Text>;

    return includedGasses.length > 0 ? gassesLabels : "";
  };

  const mapCalculationBoundaryAnswers = (answerString) => {
    return t(`questions.calculationBoundaryOptions.${answerString}`, { defaultValue: answerString ?? "" });
  };

  const mapBooleanAnswer = (answerString) => {
    return answerString === "true" ? t(`questions.booleanOptions.yes`) :
      answerString === "false" ? t(`questions.booleanOptions.no`) : answerString;
  };

  const mapTurnoverPrefix = (answerString) => {
    return answerString !== null && answerString !== "" ? CURRENCY_SYMBOLS.GBP : null
  }

  const mapQuestionAnswers = (attrKey) => {
    switch (attrKey) {
      case "greenhouseGases":
        return data[attrKey].map(obj => {
          return {
            ...obj,
            customAnswer: mapGHGAnswers(obj["answer"])
          };
        });
      case "greenhouseGasesAccountingStandards":
        return data[attrKey].map(obj => {
          return {
            ...obj,
            answer: obj.answer?.length > 0 ?
              obj.answer
                .map(el => t(`questions.greenhouseGasesAccountingStandardsOptions.${el}`, { defaultValue: el }))
                .join(", ") : ""
          }
        });
      case "calculationBoundary":
        return data[attrKey].map(obj => {
          return {
            ...obj,
            answer: mapCalculationBoundaryAnswers(obj["answer"])
          };
        });
      case "emissionVerifiedIndependently":
      case "companyPurchasedCarbonOffsetInLastTwelveMonths":
        return data[attrKey].map(obj => {
          return {
            ...obj,
            answer: mapBooleanAnswer(obj["answer"])
          };
        });
      case "turnover":
        return data[attrKey].map(obj => {
          return {
            ...obj,
            prefix: mapTurnoverPrefix(obj["answer"])
          };
        });
      default:
        return data[attrKey];
    }
  };

  return (
    <>
      <Flex
        paddingBottom={data && data.turnover.length > 0 ? 6 : 0}
        justifyContent="space-between"
        alignItems="center"
        display={dynamicStylesProps.exportContainerDisplay}
      >
        <BlockHeader
          title={t("title")}
          description={t("subTitle")}
        />
        <Button
          colorScheme="customScheme"
          type="submit"
          title={suppliersDataTranslation("exportBtnTitle")}
          width="max-content"
          onClick={handleExportClick}
        >
          {suppliersDataTranslation("exportBtn")}
        </Button>
      </Flex>
      {data && data.turnover.length > 0 ?
        <Accordion defaultIndex={[0]} allowToggle={true}>
          {Object.keys(data).map(attrKey => {
            const answersArr = mapQuestionAnswers(attrKey);
            return <QuestionAccordionItem
              key={`${attrKey}-question`}
              question={t(`questions.${attrKey}`)}
              answers={answersArr}
            />;
          })}
        </Accordion> :
        <MissingDataLabel />
      }
    </>
  );
}

export default GeneralQuestions;